<template>
    <ModalShell :title="title" :subtitle="lead" width="sm:max-w-[480px] sm:w-full w-11/12">
        <template #icon>
            <PhUsersThree :size="24" weight="regular" aria-hidden="true" />
        </template>
        <template #header-action>
            <button type="button" class="dependents-close" aria-label="Close and keep auto-renew on" @click="keep">
                <PhX :size="20" aria-hidden="true" />
            </button>
        </template>

        <div class="dependents-body">
            <GiftDependentsList :dependents="dependents" />
            <p class="pb-supporting dependents-loss" data-testid="dependents-loss">{{ DEPENDENTS_LOSE }}</p>
        </div>

        <template #footer>
            <!-- Keeping them is the weighted answer and going ahead is the edge beside it: both are
                 one press, at one height, in plain view. Wrapping puts the weighted one on top. -->
            <div class="dependents-actions">
                <PrimeButton
                    variant="secondary"
                    label="Turn off auto-renew anyway"
                    data-testid="dependents-turn-off"
                    @click="turnOff"
                />
                <PrimeButton :label="keepLabel" data-testid="dependents-keep" @click="keep" />
            </div>
        </template>
    </ModalShell>
</template>

<script setup lang="ts">
import { PhUsersThree, PhX } from '@phosphor-icons/vue';
import GiftDependentsList from 'pb-billing-ui/GiftDependentsList.vue';
import { DEPENDENTS_LOSE, dependentsLead, keepProtectingLabel, revokeQuestion } from 'pb-billing-ui/gift-dependents.ts';
import PrimeButton from 'primevue/button';
import { computed } from 'vue';
import ModalShell from '~/components/modals/shared/ModalShell.vue';
import { getDialogRef } from '~/composables/dynamic-dialog';
import { ModalNames } from '~/types/modals';

const dialogRef = getDialogRef(ModalNames.GIFT_DEPENDENTS);

/** In the order they accepted: the title names the first of them, the list names them all. */
const dependents = computed(() => dialogRef.value.data.dependents);

const title = computed(() => revokeQuestion(dependents.value));

/** What turning auto-renew off does to them, said before the list that names them. */
const lead = computed(() => dependentsLead(dependents.value, 'renewal-off'));

const keepLabel = computed(() => keepProtectingLabel(dependents.value));

// The one way to go ahead. Every other way out (keep, the close control, Escape) closes with no
// payload, and the opener reads that as auto-renew staying on.
function turnOff() {
    dialogRef.value.close({ confirmed: true });
}

function keep() {
    dialogRef.value.close();
}
</script>

<style lang="scss" scoped>
.dependents-close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: var(--touch-target-min);
    height: var(--touch-target-min);
    border-radius: var(--radius-full);
    color: var(--ink-muted);

    &:hover {
        background: var(--surface-sunken);
        color: var(--ink-body);
    }
}

.dependents-body {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
    width: 100%;
}

.dependents-loss {
    margin: 0;
}

.dependents-actions {
    display: flex;
    flex: 1 1 auto;
    flex-wrap: wrap-reverse;
    justify-content: flex-end;
    gap: var(--space-2);
    min-width: 0;
}
</style>
